import {
  type ProductCatalogManifest,
  type ProductCatalogPreparedRow,
  type ProductCatalogProductsQuery,
  productCatalogActivateSchema,
  productCatalogBindingCreateSchema,
  productCatalogBindingSchema,
  productCatalogCreateSchema,
  productCatalogImportBatchSchema,
  productCatalogImportCreateSchema,
  productCatalogProductSchema,
  productCatalogProductsPageSchema,
  productCatalogRevisionSchema,
  productCatalogSummarySchema,
  productSupplierCreateSchema,
  productSupplierDeclarationCreateSchema,
  productSupplierProductLinkCreateSchema,
  productSupplierProductLinkSchema,
  productSupplierSchema,
} from "@backchannel/tax-contracts";
import { z } from "zod";
import type { ApiRequest, Transport } from "./transport";

type Input<T extends z.ZodType> = z.input<T>;

/** Flat methods are wrapped by the client's normal response/error guard. Keys
 * are supplied by the operation and retained across uncertain write outcomes. */
export function productCatalogMethods(transport: Transport) {
  async function read<T>(
    schema: { parse: (body: unknown) => T },
    path: string,
    query?: ApiRequest["query"],
    signal?: AbortSignal,
  ) {
    return schema.parse((await transport({ method: "GET", path, query, signal })).body);
  }
  async function write<T>(
    schema: { parse: (body: unknown) => T },
    method: "POST" | "PUT",
    path: string,
    body: unknown,
    idempotencyKey: string,
  ) {
    return schema.parse(
      (await transport({ method, path, body, idempotencyKey, timeoutMs: 60_000 })).body,
    );
  }
  const catalog = (id: string) => `/v1/product-catalogs/${encodeURIComponent(id)}`;
  const revision = (id: string, revisionId: string) =>
    `${catalog(id)}/imports/${encodeURIComponent(revisionId)}`;
  return {
    listProductCatalogs: (signal?: AbortSignal) =>
      read(
        z.object({ items: z.array(productCatalogSummarySchema) }),
        "/v1/product-catalogs",
        undefined,
        signal,
      ),
    listCatalogProducts: (
      catalogId: string,
      query: ProductCatalogProductsQuery,
      signal?: AbortSignal,
    ) =>
      read(
        productCatalogProductsPageSchema,
        `${catalog(catalogId)}/products`,
        { ...query },
        signal,
      ),
    getCatalogProduct: (catalogId: string, productId: string, signal?: AbortSignal) =>
      read(
        productCatalogProductSchema,
        `${catalog(catalogId)}/products/${encodeURIComponent(productId)}`,
        undefined,
        signal,
      ),
    createProductCatalog: (body: Input<typeof productCatalogCreateSchema>, key: string) =>
      write(
        productCatalogSummarySchema,
        "POST",
        "/v1/product-catalogs",
        productCatalogCreateSchema.parse(body),
        key,
      ),
    createProductCatalogImport: (
      catalogId: string,
      manifest: ProductCatalogManifest,
      key: string,
    ) =>
      write(
        productCatalogRevisionSchema,
        "POST",
        `${catalog(catalogId)}/imports`,
        productCatalogImportCreateSchema.parse({ manifest }),
        key,
      ),
    appendProductCatalogRows: (
      catalogId: string,
      revisionId: string,
      rows: ProductCatalogPreparedRow[],
      key: string,
    ) =>
      write(
        productCatalogRevisionSchema,
        "PUT",
        `${revision(catalogId, revisionId)}/rows`,
        productCatalogImportBatchSchema.parse({ rows }),
        key,
      ),
    activateProductCatalogImport: (
      catalogId: string,
      revisionId: string,
      body: Input<typeof productCatalogActivateSchema>,
      key: string,
    ) =>
      write(
        productCatalogRevisionSchema,
        "POST",
        `${revision(catalogId, revisionId)}/activate`,
        productCatalogActivateSchema.parse(body),
        key,
      ),
    listProductSuppliers: (signal?: AbortSignal) =>
      read(
        z.object({ items: z.array(productSupplierSchema) }),
        "/v1/product-suppliers",
        undefined,
        signal,
      ),
    createProductSupplier: (body: Input<typeof productSupplierCreateSchema>, key: string) =>
      write(
        productSupplierSchema,
        "POST",
        "/v1/product-suppliers",
        productSupplierCreateSchema.parse(body),
        key,
      ),
    declareProductSupplier: (
      supplierId: string,
      body: Input<typeof productSupplierDeclarationCreateSchema>,
      key: string,
    ) =>
      write(
        productSupplierSchema,
        "POST",
        `/v1/product-suppliers/${encodeURIComponent(supplierId)}/declarations`,
        productSupplierDeclarationCreateSchema.parse(body),
        key,
      ),
    bindProductCatalog: (
      catalogId: string,
      revisionId: string,
      body: Input<typeof productCatalogBindingCreateSchema>,
      key: string,
    ) =>
      write(
        productCatalogBindingSchema,
        "POST",
        `${revision(catalogId, revisionId)}/supplier-bindings`,
        productCatalogBindingCreateSchema.parse(body),
        key,
      ),
    linkSupplierCatalogProduct: (
      supplierId: string,
      body: Input<typeof productSupplierProductLinkCreateSchema>,
      key: string,
    ) =>
      write(
        productSupplierProductLinkSchema,
        "POST",
        `/v1/product-suppliers/${encodeURIComponent(supplierId)}/product-links`,
        productSupplierProductLinkCreateSchema.parse(body),
        key,
      ),
  };
}
